import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage } from '../i18n/LanguageContext';
import { 
  AlertCircle, 
  Clock, 
  XCircle, 
  CheckCircle, 
  Mail,
  RefreshCw,
  DollarSign,
  Calendar,
  MapPin
} from 'lucide-react';

const CancellationPolicyPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <WhatsAppButton />

      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#1D4E41] to-[#2d6a4f]">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('cancellation.title')}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {t('cancellation.subtitle')}
          </p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          
          {/* Important Notice */}
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 mb-10">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-amber-800 mb-2">{t('cancellation.importantNotice')}</h2>
                <p className="text-amber-700">
                  {t('cancellation.importantNoticeText')}
                </p>
              </div>
            </div>
          </div>

          {/* Pickup Information Requirements */}
          <div className="bg-white rounded-2xl border-2 border-[#1D4E41] overflow-hidden shadow-lg mb-10">
            <div className="bg-[#1D4E41] px-6 py-5">
              <div className="flex items-center gap-3">
                <MapPin className="w-7 h-7 text-white" />
                <h2 className="text-xl font-bold text-white">{t('cancellation.pickupTitle')}</h2>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-6 text-lg">
                {t('cancellation.pickupDesc')}
              </p>
              
              {/* TRANSFERS SECTION */}
              <div className="mb-8">
                <div className="bg-[#1D4E41] text-white px-4 py-2 rounded-t-xl">
                  <h3 className="font-bold text-lg">{t('cancellation.forTransfers')}</h3>
                  <p className="text-white/70 text-sm">{t('cancellation.transfersDesc')}</p>
                </div>
                
                <div className="border-2 border-[#1D4E41] border-t-0 rounded-b-xl p-5">
                  {/* Service Area */}
                  <div className="bg-blue-50 rounded-xl p-4 mb-4 border border-blue-200">
                    <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      {t('cancellation.coverageArea')}
                    </h4>
                    <ul className="text-blue-700 space-y-1 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span><strong>{t('cancellation.airportZone')}</strong> {t('cancellation.airportZoneDesc')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span><strong>{t('cancellation.losSuenosResort')}</strong> {t('cancellation.losSuenosDesc')}</span>
                      </li>
                    </ul>
                  </div>

                  {/* Required Information for Transfers */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Arrivals */}
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" transform="rotate(180 12 12)" />
                        </svg>
                        {t('cancellation.arriving')}
                      </h4>
                      <p className="text-green-600 text-xs mb-3 italic">{t('cancellation.arrivingSubtitle')}</p>
                      <ul className="text-green-700 text-sm space-y-1">
                        <li>• {t('cancellation.arrivalFlight')}</li>
                        <li>• {t('cancellation.arrivalDate')}</li>
                        <li>• {t('cancellation.airline')}</li>
                        <li>• {t('cancellation.destAddress')}</li>
                        <li>• {t('cancellation.numPassengers')}</li>
                        <li>• {t('cancellation.contactPhone')}</li>
                      </ul>
                    </div>
                    
                    {/* Departures */}
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                        {t('cancellation.departing')}
                      </h4>
                      <p className="text-orange-600 text-xs mb-3 italic">{t('cancellation.departingSubtitle')}</p>
                      <ul className="text-orange-700 text-sm space-y-1">
                        <li>• {t('cancellation.departureFlight')}</li>
                        <li>• {t('cancellation.departureDate')}</li>
                        <li>• {t('cancellation.airline')}</li>
                        <li>• {t('cancellation.pickupAddress')}</li>
                        <li>• {t('cancellation.numPassengers')}</li>
                        <li>• {t('cancellation.contactPhone')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* TOURS SECTION */}
              <div className="mb-6">
                <div className="bg-emerald-600 text-white px-4 py-2 rounded-t-xl">
                  <h3 className="font-bold text-lg">{t('cancellation.forTours')}</h3>
                  <p className="text-white/70 text-sm">{t('cancellation.toursDesc')}</p>
                </div>
                
                <div className="border-2 border-emerald-600 border-t-0 rounded-b-xl p-5">
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                    <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      {t('cancellation.requiredPickup')}
                    </h4>
                    <p className="text-emerald-700 mb-3">
                      {t('cancellation.pickupInfoDesc')}
                    </p>
                    <ul className="text-emerald-700 text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{t('cancellation.hotelName')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{t('cancellation.numPassengers')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{t('cancellation.contactPhone')}</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 mt-4">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-blue-800 mb-1">{t('cancellation.pickupTime')}</h4>
                        <p className="text-blue-700 text-sm">
                          {t('cancellation.pickupTimeDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-red-50 rounded-xl p-5 border-2 border-red-200">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-red-800 mb-2">{t('cancellation.disclaimer')}</h4>
                    <p className="text-red-700">
                      {t('cancellation.disclaimerText')}
                    </p>
                    <ul className="text-red-700 mt-3 space-y-1 text-sm">
                      <li>• {t('cancellation.disclaimer1')}</li>
                      <li>• {t('cancellation.disclaimer2')}</li>
                      <li>• {t('cancellation.disclaimer3')}</li>
                      <li>• {t('cancellation.disclaimer4')}</li>
                    </ul>
                    <p className="text-red-700 mt-3 font-medium">
                      {t('cancellation.disclaimerEnd')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Policy Cards */}
          <div className="space-y-6">
            
            {/* Customer Cancellation */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="bg-red-50 px-6 py-4 border-b border-red-100">
                <div className="flex items-center gap-3">
                  <XCircle className="w-6 h-6 text-red-600" />
                  <h3 className="text-lg font-bold text-red-800">{t('cancellation.customerCancel')}</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-red-50 rounded-xl">
                    <Clock className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-red-800">{t('cancellation.lessThan24h')}</p>
                      <p className="text-red-700 mt-1">
                        {t('cancellation.lessThan24hDesc')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-green-800">{t('cancellation.moreThan24h')}</p>
                      <p className="text-green-700 mt-1">
                        {t('cancellation.moreThan24hDesc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* No-Show Policy */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="bg-gray-800 px-6 py-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-white" />
                  <h3 className="text-lg font-bold text-white">{t('cancellation.noShow')}</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <XCircle className="w-6 h-6 text-gray-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">{t('cancellation.noShowTitle')}</p>
                    <p className="text-gray-600 mt-1">
                      {t('cancellation.noShowDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Weather Cancellation */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="bg-sky-500 px-6 py-4">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                  <h3 className="text-lg font-bold text-white">{t('cancellation.weather')}</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  {t('cancellation.weatherDesc')}
                </p>
                <div className="flex items-start gap-4 p-4 bg-sky-50 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-sky-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-sky-800">{t('cancellation.fullRefund100')}</p>
                    <p className="text-sky-700 mt-1">
                      {t('cancellation.fullRefund100Desc')}
                    </p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm mt-4">
                  {t('cancellation.weatherNote')}
                </p>
              </div>
            </div>

            {/* Company Cancellation */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="bg-[#1D4E41] px-6 py-4">
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-6 h-6 text-white" />
                  <h3 className="text-lg font-bold text-white">{t('cancellation.companyCancel')}</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  {t('cancellation.companyCancelDesc')}
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl">
                    <DollarSign className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-800">{t('cancellation.fullRefund')}</p>
                      <p className="text-green-700 text-sm mt-1">{t('cancellation.fullRefundDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl">
                    <Calendar className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-blue-800">{t('cancellation.rescheduling')}</p>
                      <p className="text-blue-700 text-sm mt-1">{t('cancellation.reschedulingDesc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* How to Cancel */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="bg-blue-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-6 h-6 text-white" />
                  <h3 className="text-lg font-bold text-white">{t('cancellation.howToCancel')}</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  {t('cancellation.howToCancelDesc')}
                </p>
                <div className="bg-gray-50 rounded-xl p-6 text-center">
                  <a 
                    href="mailto:reservations@travelguide.cr" 
                    className="text-2xl font-bold text-[#1D4E41] hover:underline"
                  >
                    reservations@travelguide.cr
                  </a>
                  <p className="text-gray-500 mt-3 text-sm">
                    {t('cancellation.includeBooking')}
                  </p>
                </div>
              </div>
            </div>

            {/* Refund Processing */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="bg-purple-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-6 h-6 text-white" />
                  <h3 className="text-lg font-bold text-white">{t('cancellation.refundTime')}</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl">
                  <Clock className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-purple-800">{t('cancellation.businessDays')}</p>
                    <p className="text-purple-700 mt-1">
                      {t('cancellation.businessDaysDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Contact for Questions */}
          <div className="mt-12 bg-[#E8F5E9] rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-[#1D4E41] mb-3">{t('cancellation.questions')}</h3>
            <p className="text-[#1D4E41] mb-6">
              {t('cancellation.questionsDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:reservations@travelguide.cr"
                className="h-12 px-8 bg-[#1D4E41] hover:bg-[#153A30] text-white rounded-full font-medium flex items-center justify-center gap-2 transition-all"
              >
                <Mail className="w-5 h-5" />
                {t('cancellation.emailUs')}
              </a>
              <a
                href="https://wa.me/50688173167"
                target="_blank"
                rel="noopener noreferrer"
                className="h-12 px-8 border-2 border-[#1D4E41] text-[#1D4E41] hover:bg-[#1D4E41] hover:text-white rounded-full font-medium flex items-center justify-center gap-2 transition-all"
              >
                {t('cancellation.whatsapp')}
              </a>
            </div>
          </div>

          {/* Last Updated */}
          <p className="text-center text-gray-400 text-sm mt-8">
            {t('cancellation.lastUpdated')}
          </p>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CancellationPolicyPage;
